import { useEffect, useRef } from 'react';
import { sendUiSettingsOnUnload, useUpdateUiSettings } from '@/entities/profile';
import { useSettingsStore } from '@/entities/settings';
import { SettingStore, SettingStoreUserValues, UiSettings } from '@/entities/settings/models/types';

const DEBOUNCE_DELAY_MS = 500;

/**
 * Хук синхронизации настроек интерфейса пользователя с бэкендом.
 *
 * Следит за изменениями пользовательских настроек в сторе и отправляет их
 * на сервер через PUT /profile/ui-settings с дебаунсом 500 мс.
 * Первоначальная инициализация (при монтировании) пропускается — сохраняются
 * только изменения, сделанные пользователем.
 *
 * Настройка, сделанная перед самым уходом со страницы, не теряется: отложенная запись
 * отправляется немедленно, как только страница скрывается или выгружается (перезагрузка,
 * закрытие вкладки, переход по внешней ссылке). Иначе таблица, чей вид или отбор поменяли
 * прямо перед перезагрузкой, возвращалась бы к прежнему состоянию.
 */
export const useUiSettingsSync = (userId: string | null) => {
  const { mutate: saveUiSettings } = useUpdateUiSettings();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Предыдущие настройки для определения факта изменения
  const prevSettingsRef = useRef<SettingStoreUserValues | null>(null);
  // Изменение, ожидающее отправки: его же отправляет уход со страницы
  const pendingSettingsRef = useRef<SettingStoreUserValues | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Снимаем baseline до подписки — это уже инициализированные настройки пользователя
    // (initUserSettings выполняется до рендера children, т.е. до этого эффекта).
    // Любое последующее изменение стора означает действие пользователя.
    prevSettingsRef.current = useSettingsStore.getState().settings[userId] ?? null;

    // Подписываемся на изменения настроек конкретного пользователя в сторе
    const unsubscribe = useSettingsStore.subscribe((state: SettingStore) => {
      const settings = state.settings[userId] ?? null;

      // Пропускаем если настройки не изменились (ссылочное сравнение)
      if (settings === prevSettingsRef.current) return;
      prevSettingsRef.current = settings;

      if (!settings) return;

      // Отменяем предыдущий таймер дебаунса
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Запускаем сохранение с дебаунсом 500 мс
      pendingSettingsRef.current = settings;
      debounceTimerRef.current = setTimeout(() => {
        pendingSettingsRef.current = null;
        saveUiSettings(settings satisfies UiSettings);
      }, DEBOUNCE_DELAY_MS);
    });

    // Уход со страницы: отложенное изменение отправляется сразу, запросом, переживающим выгрузку.
    // Слушаем оба события: `pagehide` не приходит, когда вкладку просто скрыли, а `visibilitychange`
    // может не прийти на самой выгрузке.
    const flushOnHide = () => {
      const pending = pendingSettingsRef.current;
      if (!pending) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSettingsRef.current = null;
      sendUiSettingsOnUnload(pending satisfies UiSettings);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushOnHide();
    };
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      window.removeEventListener('pagehide', flushOnHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Настройка, не успевшая уехать до смены пользователя (выход, перемонтирование дерева),
      // отправляется тем же путём: ждать её больше некому
      const pending = pendingSettingsRef.current;
      if (pending) {
        pendingSettingsRef.current = null;
        sendUiSettingsOnUnload(pending satisfies UiSettings);
      }
    };
  }, [userId, saveUiSettings]);
};
